import { DOCS_URLS } from "@p0security/frontend-core";
import type { GithubRepositoriesComponents } from "@p0security/integrations-github-repositories-shared";
import type { FrontendInstaller } from "@p0security/integrations-installer";
import { Typography } from "@p0security/ui";

import { toOrgAppsUrl } from "./app-registration.ts";
import { CLOUDS, type CloudDispatch } from "./clouds.ts";
import { type RenderedCommands, toItemCommands } from "./commands.ts";
import { ItemSteps } from "./item-steps.tsx";

const { Link, Paragraph, Text } = Typography;

/** Says how to run the connector's shell script. It sets `set -e`, which
 * would close an interactive shell it's pasted into at its first error, so
 * it's saved and run with bash. */
const ShellHeader: React.FC = () => (
  <Paragraph>
    Save this script as a file, such as <Text code>deploy-connector.sh</Text>,
    and run it with <Text code>bash deploy-connector.sh</Text>. Then click
    &quot;Next&quot;.
  </Paragraph>
);

/** The connector's shell script and Terraform as the instruction's commands,
 * if they rendered. */
const toInstructionCommands = (rendered: RenderedCommands | undefined) =>
  rendered?.terraform === undefined
    ? undefined
    : {
        iac: [{ command: rendered.terraform }],
        shell: [{ command: rendered.shell, header: <ShellHeader /> }],
      };

/** Shown above the new item's form, before the customer has entered
 * anything. */
const NewPreamble: React.FC = () => (
  <>
    <Paragraph>
      P0 grants roles on the organization&apos;s repositories through a
      connector that you deploy in your AWS account. The connector acts as a
      GitHub App that you create, and only the connector reads the app&apos;s
      private key.
    </Paragraph>
    <Paragraph>
      Have ready: P0&apos;s AWS IAM management and AWS Secrets Manager
      integration; an owner of the GitHub organization, to install the app; and
      Terraform or bash, with Docker and the AWS CLI, to deploy the connector.
      See{" "}
      <Link
        href={DOCS_URLS.GITHUB_REPOSITORIES_BEFORE_YOU_BEGIN}
        rel="noopener noreferrer"
        target="_blank"
      >
        Before you begin
      </Link>
      .
    </Paragraph>
    <Paragraph>
      Below, say where in AWS the private key will be stored and the connector
      will run, and name the secret that will hold the key. The key goes in AWS
      Secrets Manager and the connector runs on AWS Lambda, the only options for
      now. These can&apos;t change once the organization is added. The next page
      shows how to create the GitHub App, save its key in that secret, install
      the app, and deploy the connector.
    </Paragraph>
  </>
);

/** The private key secret field's description. */
const SECRET_DESCRIPTION = `${CLOUDS.aws.vault.secretDescription} The connector's Terraform or shell script grants it read on exactly this secret, so it can't change once the organization is added.`;

/** A connector name field's description, for a connector that runs as
 * `runtime`. */
const toConnectorNameDescription = (runtime: string) =>
  `The name of the connector's ${runtime}, which its Terraform or shell script creates. Each organization needs a connector of its own.`;

/** A region field's description, for what's in `cloud`'s region. */
const toRegionDescription = (
  { label, regionPlaceholder }: CloudDispatch,
  subject: string
) => `The ${label} region ${subject}, such as ${regionPlaceholder}.`;

/**
 * Installs an organization's GitHub Repositories access.
 *
 * The framework's new-item form takes the org's login and where the key and
 * the connector go, which can't change after. The item's page then shows the
 * steps to create the GitHub App, save its key, install it and deploy the
 * connector, with the connector's Terraform and shell script, above the "Next" that verifies
 * the item. Its configure page asks for the App ID, and "Finish" validates the
 * install through the connector.
 */
export const githubRepositoriesInstaller: FrontendInstaller<
  typeof GithubRepositoriesComponents
> = {
  components: {
    "repository-access": {
      newPreamble: () => <NewPreamble />,
      // The new item's form describes the org's login, its item id, with the
      // component's labeler.
      labeler: () =>
        "The organization's login, not its URL. For github.com/acme, enter acme. Each organization gets its own GitHub App and connector.",
      // The item was saved with every field the commands need, so they
      // render from what the item stores.
      instructions: (context, id, item) => {
        const rendered = toItemCommands(context, id, item);
        return {
          help: <ItemSteps item={item} org={id} rendered={rendered} />,
          commands: toInstructionCommands(rendered),
        };
      },
      // The framework passes the configure form's draft, which keeps the App
      // ID entered whether or not "Finish" succeeded, so the steps' state
      // comes from the item P0 stored.
      preamble: (context, id) => {
        const stored = context.config?.["repository-access"]?.[id];
        return stored?.state === "configure" ? (
          <ItemSteps
            item={stored}
            org={id}
            rendered={toItemCommands(context, id, stored)}
          />
        ) : undefined;
      },
      items: {
        appId: {
          labeler: (_context, id) => (
            <>
              Find it on the app&apos;s settings page, under About: open{" "}
              <Link
                href={toOrgAppsUrl(id)}
                rel="noopener noreferrer"
                target="_blank"
              >
                {id}&apos;s GitHub Apps
              </Link>{" "}
              and click Edit beside the app.
            </>
          ),
        },
        privateKeySecretName: {
          labeler: () => SECRET_DESCRIPTION,
        },
        vault: {
          labeler: () =>
            "Where the GitHub App's private key is stored. GitHub Repositories supports only AWS Secrets Manager for now.",
          options: {
            "aws-sm": {
              items: {
                install: {
                  labeler: () => CLOUDS.aws.vault.install.description,
                  optionProvider: CLOUDS.aws.vault.install.installs,
                },
                secretsRegion: {
                  labeler: () =>
                    toRegionDescription(CLOUDS.aws, "the secret is in"),
                },
              },
            },
          },
        },
        hosting: {
          labeler: () =>
            "Where the connector runs. GitHub Repositories supports only AWS Lambda for now.",
          options: {
            aws: {
              items: {
                accountId: {
                  labeler: () => CLOUDS.aws.hosting.description,
                  optionProvider: CLOUDS.aws.hosting.installs,
                },
                connectorName: {
                  labeler: () => toConnectorNameDescription(CLOUDS.aws.runtime),
                },
                connectorRegion: {
                  labeler: () =>
                    toRegionDescription(CLOUDS.aws, "the connector runs in"),
                },
              },
            },
          },
        },
      },
    },
  },
};
